// Card Image Utility
// Maps card names to image URLs in the local assets and Supabase storage

const CARD_IMAGE_BASE_URL = 'https://hvswbgwnsymvfblxgrxq.supabase.co/storage/v1/object/public/crossroads-card-images/';

/**
 * Convert card name to image filename
 * "Ember Imp" → "EmberImp.png"
 * "Herald of Dawn" → "HeraldOfDawn.png"
 */
export function cardNameToImageFilename(cardName: string): string {
  // Remove special characters and convert to CamelCase
  return cardName
    .replace(/[''',]/g, '') // Remove apostrophes and commas
    .split(/[\s-]+/) // Split on spaces and hyphens
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('') + '.png';
}

/**
 * Map commander names to their card front filenames
 */
const COMMANDER_IMAGE_MAP: Record<string, string> = {
  'Dice and Autism': 'CardFrontFrankRed.png',
  'Nic': 'CardFrontNic.png',
  'Carma': 'CardFrontCarma.png',
  'Neal': 'CardFrontNeal.png',
  'Brandy': 'CardFrontBrandy.png',
  'Alli': 'CardFrontAlli.png',
  'Doug': 'CardFrontDoug.png',
};

/**
 * Get full image URL for a card by name
 */
export function getCardImageUrl(cardName: string): string {
  // Check if this is a commander card
  const commanderFilename = COMMANDER_IMAGE_MAP[cardName];
  if (commanderFilename) {
    return `${CARD_IMAGE_BASE_URL}${commanderFilename}`;
  }

  // Otherwise use the standard naming convention
  const filename = cardNameToImageFilename(cardName);
  return `${CARD_IMAGE_BASE_URL}${filename}`;
}

/**
 * Get local image path for a card
 */
export function getLocalCardImagePath(cardName: string): string {
  const filename = cardNameToImageFilename(cardName);
  return `/src/components/games/crossroads/cardImages/${filename}`;
}

/**
 * Preload a card image
 */
export function preloadCardImage(cardName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = getCardImageUrl(cardName);
  });
}

/**
 * Check if card image exists locally
 */
export async function cardImageExists(cardName: string): Promise<boolean> {
  try {
    await preloadCardImage(cardName);
    return true;
  } catch {
    return false;
  }
}

/**
 * Preload all commander images for instant display
 */
export function preloadCommanderImages(): Promise<void[]> {
  const commanders = [
    'Dice and Autism',
    'Nic',
    'Carma',
    'Neal',
    'Brandy',
    'Alli',
    'Doug',
  ];
  return Promise.all(commanders.map(name => preloadCardImage(name)));
}

/**
 * Preload multiple card images with priority
 * Priority images are loaded first, then others
 */
export async function preloadImagesWithPriority(
  priorityCards: string[],
  otherCards: string[]
): Promise<void> {
  // Load priority images first
  await Promise.all(priorityCards.map(name => preloadCardImage(name)));

  // Then load other images in the background
  Promise.all(otherCards.map(name => preloadCardImage(name))).catch(() => {
    // Silently fail for background preloading
  });
}
